/**
 * Domain Registry — Heart of Science v2
 * Central configuration for all 4 domains (CEO-approved, Sprint 2)
 * Drives domain-adaptive UI, filtering, and validation
 * Last Updated: 2026-04-03
 */

export type Domain =
  | 'biotech'
  | 'medtech'
  | 'deeptech'
  | 'shallowtech'

export interface ProfileField {
  key: string
  label: string
  type: 'text' | 'enum' | 'boolean' | 'url'
  enum_values?: string[]
  description?: string
}

export interface SubdomainConfig {
  id: string
  label: string
  icon: string
  description?: string
}

export interface DomainConfig {
  label: string
  color: string // hex value
  tailwindColor: string // e.g. 'domain-biotech'
  icon: string // emoji or icon name
  profileFields: ProfileField[]
  filterFields: string[]
  description?: string
  subdomains?: SubdomainConfig[]
}

export const DOMAIN_REGISTRY: Record<Domain, DomainConfig> = {
  // ==================== BIOTECH ====================
  biotech: {
    label: 'Biotech',
    color: '#10B981',
    tailwindColor: 'domain-biotech',
    icon: '🧬',
    description: 'Drug discovery, therapeutics, gene & cell therapy, translational bio. Core IP: biological/chemical entity for human medicine',
    profileFields: [
      {
        key: 'target',
        label: 'Target',
        type: 'text',
        description: 'Protein target or disease target',
      },
      {
        key: 'mechanism_of_action',
        label: 'Mechanism of Action',
        type: 'text',
        description: 'How the drug/therapy works',
      },
      {
        key: 'modality',
        label: 'Modality',
        type: 'enum',
        enum_values: [
          'SMALL_MOLECULE',
          'BIOLOGIC',
          'CELL_THERAPY',
          'GENE_THERAPY',
          'RNA',
          'PEPTIDE',
          'ADC',
          'OTHER',
        ],
      },
      {
        key: 'therapeutic_area',
        label: 'Therapeutic Area',
        type: 'text',
        description: 'e.g., Oncology, Cardiology, Immunology',
      },
      {
        key: 'therapeutic_group',
        label: 'Therapeutic Group',
        type: 'text',
        description: 'Broader classification',
      },
      {
        key: 'dev_phase',
        label: 'Development Phase',
        type: 'enum',
        enum_values: [
          'BASIC_RESEARCH',
          'PRECLINICAL',
          'PHASE_1',
          'PHASE_2',
          'PHASE_3',
          'APPROVED',
        ],
      },
      {
        key: 'competitive_position',
        label: 'Competitive Position',
        type: 'enum',
        enum_values: ['FIRST_IN_CLASS', 'BEST_IN_CLASS', 'FAST_FOLLOWER', 'ME_TOO'],
      },
      {
        key: 'ip_status',
        label: 'IP Status',
        type: 'text',
        description: 'Patent status or protection',
      },
      {
        key: 'clinical_trial_id',
        label: 'Clinical Trial ID',
        type: 'text',
        description: 'ClinicalTrials.gov identifier',
      },
      {
        key: 'indication',
        label: 'Indication',
        type: 'text',
        description: 'Disease indication being targeted',
      },
    ],
    filterFields: [
      'modality',
      'dev_phase',
      'therapeutic_area',
      'competitive_position',
    ],
    subdomains: [
      {
        id: 'drug',
        label: 'Drug',
        icon: '💊',
        description: 'Therapeutics: small molecules, biologics, cell/gene, RNA, ADCs — discovery through clinical trials',
      },
      {
        id: 'diagnostics',
        label: 'Diagnostics',
        icon: '🔬',
        description: 'In vitro diagnostics, biomarkers, companion diagnostics — IVD regulatory path',
      },
      {
        id: 'platform',
        label: 'Platform',
        icon: '⚙️',
        description: 'Enabling tech: drug delivery, screening, biomaterials, tooling — licensing model',
      },
    ],
  },

  // ==================== MEDTECH ====================
  medtech: {
    label: 'Medtech',
    color: '#06B6D4',
    tailwindColor: 'domain-medtech',
    icon: '🩺',
    description: 'Medical devices, implants, diagnostic hardware, clinical-grade sensors, lab equipment. Core IP: physical device for clinical use',
    profileFields: [],
    filterFields: [],
  },

  // ==================== DEEPTECH ====================
  deeptech: {
    label: 'Deeptech',
    color: '#8B5CF6',
    tailwindColor: 'domain-deeptech',
    icon: '🚀',
    description: 'Space, materials, energy, quantum, robotics, agritech, foodtech, photonics, defense, cybersecurity, industrial IoT. Core IP: advanced science/engineering outside medicine',
    profileFields: [],
    filterFields: [],
  },

  // ==================== SHALLOWTECH ====================
  shallowtech: {
    label: 'Shallowtech',
    color: '#3B82F6',
    tailwindColor: 'domain-shallowtech',
    icon: '😢',
    description: 'Fintech, proptech, healthtech digital, edtech, SaaS, e-commerce, consumer apps, legaltech, govtech. Innovation: business model or software, not proprietary science/hardware',
    profileFields: [],
    filterFields: [],
  },
}

/**
 * Pre-built Tailwind class maps
 * Avoids dynamic interpolation which won't survive Tailwind purge
 * Usage: className={DOMAIN_BADGE_CLASSES[domain]}
 */

export const DOMAIN_BADGE_CLASSES: Record<Domain, string> = {
  biotech: 'bg-[#0596691a] text-[#059669]',
  medtech: 'bg-[#06B6D41a] text-[#06B6D4]',
  deeptech: 'bg-[#8B5CF61a] text-[#8B5CF6]',
  shallowtech: 'bg-[#F59E0B1a] text-[#F59E0B]',
}

export const DOMAIN_CHIP_ACTIVE_CLASSES: Record<Domain, string> = {
  biotech: 'bg-domain-biotech/20 text-domain-biotech border-domain-biotech/30',
  medtech: 'bg-domain-medtech/20 text-domain-medtech border-domain-medtech/30',
  deeptech: 'bg-domain-deeptech/20 text-domain-deeptech border-domain-deeptech/30',
  shallowtech: 'bg-domain-shallowtech/20 text-domain-shallowtech border-domain-shallowtech/30',
}

export const DOMAIN_BADGE_DARK_CLASSES: Record<Domain, string> = {
  biotech: 'bg-domain-biotech text-text-inverse',
  medtech: 'bg-domain-medtech text-text-inverse',
  deeptech: 'bg-domain-deeptech text-text-inverse',
  shallowtech: 'bg-domain-shallowtech text-text-inverse',
}

/**
 * Helper to get domain config by domain key
 */
export function getDomainConfig(domain: string): DomainConfig | undefined {
  return DOMAIN_REGISTRY[domain as Domain]
}

/**
 * Get all domains as array
 */
export function getAllDomains(): Array<Domain> {
  return Object.keys(DOMAIN_REGISTRY) as Domain[]
}
